import { useTranslation } from 'react-i18next';

import { DEFAULT_PATTERN, type Look, type LookStyle, STANDARD_LOOK } from '@/entities/appearance';
import { ColorSwatches } from '@/shared/ui';

import styles from './editor.module.css';
import { GradientEditor } from './GradientEditor';
import { isColoredStyle, withOneColor, withStyle } from './look-model';
import { PatternEditor } from './PatternEditor';

interface Props {
  /** `null` = the standard (neutral) look. */
  value: Look | null;
  onChange: (value: Look | null) => void;
  /** Styles offered besides "standard" (a column offers all of them). */
  allowed: readonly LookStyle[];
  /** Used as the starting color of a style. */
  idPrefix: string;
}

/**
 * Edit a {@link Look} with progressive disclosure: pick a style; "One color" (on by default)
 * shows a single picker, switching it off reveals per-element colors; gradient and pattern have
 * their own panels. The parent renders the live preview next to it.
 */
export function LookEditor({ value, onChange, allowed, idPrefix }: Props) {
  const { t } = useTranslation();
  const look = value ?? STANDARD_LOOK;
  const style = look.style;
  const choices: LookStyle[] = ['standard', ...allowed];
  const advanced = look.advanced;

  return (
    <div className={styles.section}>
      <div className={styles.options} role="radiogroup" aria-label={t('appearance.style')}>
        {choices.map((choice) => (
          <button
            key={choice}
            type="button"
            role="radio"
            aria-checked={style === choice}
            className={styles.option}
            data-testid={`${idPrefix}-style-${choice}`}
            onClick={() => {
              onChange(withStyle(value, choice));
            }}
          >
            <strong>{t(`appearance.styles.${choice}`)}</strong>
            <small>{t(`appearance.styles.${choice}Hint`)}</small>
          </button>
        ))}
      </div>

      {isColoredStyle(style) && (
        <>
          <label className={styles.switch}>
            <input
              type="checkbox"
              role="switch"
              checked={look.one_color}
              onChange={(event) => {
                onChange(withOneColor(look, event.target.checked));
              }}
            />
            <span>{t('appearance.oneColor')}</span>
          </label>
          {look.one_color ? (
            <ColorSwatches
              label={t('appearance.color')}
              value={look.color}
              onChange={(color) => {
                if (color) onChange({ ...look, color });
              }}
            />
          ) : (
            <>
              <p className={styles.hint}>{t('appearance.advancedHint')}</p>
              {(['header', 'border', 'body'] as const).map((part) => (
                <ColorSwatches
                  key={part}
                  label={t(`appearance.parts.${part}`)}
                  value={advanced?.[part] ?? null}
                  noneLabel={t('appearance.partNone')}
                  onChange={(color) => {
                    onChange({
                      ...look,
                      advanced: {
                        header: advanced?.header ?? null,
                        border: advanced?.border ?? null,
                        body: advanced?.body ?? null,
                        [part]: color,
                      },
                    });
                  }}
                />
              ))}
            </>
          )}
        </>
      )}

      {style === 'gradient' && look.gradient && (
        <GradientEditor
          value={look.gradient}
          onChange={(gradient) => {
            onChange({ ...look, gradient });
          }}
        />
      )}
      {style === 'pattern' && (
        <PatternEditor
          value={look.pattern ?? DEFAULT_PATTERN}
          onChange={(pattern) => {
            onChange({ ...look, pattern });
          }}
        />
      )}
    </div>
  );
}
